import { useAPI, useQueryParams } from "hooks";
import { Loader, PaginationNumbers, PaginationSize, Select } from "shared";
import { TBlogsData, TFilterCategory } from "components/Blog/Blog.types";
import { TOption } from "shared/Select/Select";
import { useMemo } from "react";
import * as R from "ramda";
import { BlogQueryParams } from "components/Blog/Blog.constants";
import {
  constructApiUrlSearchQuery,
  isShowAllCategory,
} from "components/Blog/helpers";
import { TBlogItemProps } from "../BlogItem/BlogItem";

type TBlogListProps = {
  activeCategory?: TFilterCategory;
  sortOptions: TOption[];
  isMobile: boolean;
  apiBaseUrl: string;
  sortByText: string;
  BlogItem: React.FC<TBlogItemProps>;
  articlesText?: string;
  paginationStartText?: string;
  paginationEndText?: string;
  blogItemCtaText?: string;
  defaultQueryParams: any;
};

function BlogList({
  activeCategory,
  sortOptions,
  isMobile,
  apiBaseUrl,
  sortByText,
  BlogItem,
  articlesText,
  paginationStartText,
  paginationEndText,
  blogItemCtaText,
  defaultQueryParams,
}: TBlogListProps) {
  const { queryParams, setQueryParams } = useQueryParams();

  const newQueryParams = R.mergeLeft(queryParams, defaultQueryParams);
  const pageSize = Number(R.prop(BlogQueryParams.PageSize, newQueryParams));
  const activePage = Number(R.prop(BlogQueryParams.ActivePage, newQueryParams));
  const sortValue = R.prop(BlogQueryParams.SortBy, newQueryParams);

  console.log(pageSize, activePage, sortValue);
  const { data, isLoading } = useAPI<TBlogsData>({
    url:
      apiBaseUrl +
      "BlogSearch?" +
      constructApiUrlSearchQuery(
        newQueryParams,
        isShowAllCategory(activeCategory) ? "" : activeCategory?.Title ?? ""
      ),
    method: "GET",
  });

  const setPageSize = (page: number) =>
    setQueryParams({
      ...newQueryParams,
      [BlogQueryParams.PageSize]: page,
      [BlogQueryParams.ActivePage]: 1,
    });

  const setActivePage = (page: number) =>
    setQueryParams({ ...queryParams, [BlogQueryParams.ActivePage]: page });

  const setSortOption = (option: TOption) =>
    setQueryParams({
      ...newQueryParams,
      [BlogQueryParams.SortBy]: option.value,
      [BlogQueryParams.ActivePage]: 1,
    });

  const activeSortOption = useMemo(
    () => R.find((option) => option.value === sortValue, sortOptions),
    [sortOptions, sortValue]
  );

  const areResultsDisplayed = Boolean(data?.BlogList?.length);

  return (
    <div className="o-blog-list">
      <div className="o-blog-list__content">
        <div className="o-blog-list__header">
          {activeCategory && <h2>{activeCategory.Title}</h2>}
          {!isMobile && areResultsDisplayed && (
            <span>
              {data
                ? `${data?.TotalCount} ${
                    articlesText ? articlesText : "articles"
                  }`
                : ""}
            </span>
          )}
          <Select
            label={sortByText}
            options={sortOptions}
            value={activeSortOption}
            isSearchable={false}
            onChange={(option) => setSortOption(option as TOption)}
          />
          {isMobile && areResultsDisplayed && (
            <span>{`(${data?.TotalCount})`}</span>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            {areResultsDisplayed ? (
              <div className="o-blog-list__items">
                {[...(data?.BlogList || [])]?.map((blog, idx) => (
                  <BlogItem
                    blog={blog}
                    key={`blog-${blog.Id}-${idx}`}
                    ctaText={blogItemCtaText}
                  />
                ))}
              </div>
            ) : (
              <div className="o-blog-list__no-results">
                {data?.NoResultsText}
              </div>
            )}
          </>
        )}

        {data?.TotalCount && areResultsDisplayed && (
          <div className="o-blog-list__pagination">
            <PaginationNumbers
              activePage={activePage}
              noOfPages={Math.ceil(Number(data.TotalCount) / pageSize)}
              onChange={(page) => setActivePage(page)}
            />
            <PaginationSize
              startText={paginationStartText || "Showing"}
              endText={paginationEndText || "per page"}
              value={pageSize}
              onChange={(value) => setPageSize(value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogList;
